import { useEffect, useState } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { useLanguage } from "../contexts/LanguageContext";
import { useAuth } from "../contexts/AuthContext";
import CustomerLayout from "../components/CustomerLayout";
import api from "../lib/api";
import { toast } from "sonner";
import { Wallet, Trash2 } from "lucide-react";

const ApiBalancePage = () => {
  const { language } = useLanguage();
  const { refreshUser, user } = useAuth();
  const [balance, setBalance] = useState(0);
  const [amount, setAmount] = useState("100");
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [recent, setRecent] = useState([]);
  const [pushPrice, setPushPrice] = useState(10);
  const [minTopup, setMinTopup] = useState(10);
  const [maxTopup, setMaxTopup] = useState(5000);

  const load = async () => {
    try {
      const res = await api.get("/me/api-balance");
      setBalance(res.data.api_balance || 0);
      setPushPrice(res.data.push_price_usd || 15);
      setMinTopup(res.data.topup_min_usd || 10);
      setMaxTopup(res.data.topup_max_usd || 5000);
      setRecent(res.data.recent_topups || []);
      await refreshUser?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bakiye alınamadı");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const topup = async () => {
    const n = parseFloat(amount);
    if (!n || n < minTopup || n > maxTopup) {
      toast.error(language === "tr"
        ? `Tutar $${minTopup}–$${maxTopup} arası olmalı`
        : `Amount must be $${minTopup}–$${maxTopup}`);
      return;
    }
    setPaying(true);
    try {
      const res = await api.post("/me/api-balance/topup", {
        amount_usd: n,
        origin_url: window.location.origin,
        pay_currency: "usdttrc20",
      });
      if (res.data.invoice_url) {
        window.location.href = res.data.invoice_url;
      } else {
        throw new Error("invoice_url yok");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Ödeme başlatılamadı");
      setPaying(false);
    }
  };

  const displayBal = user?.api_balance ?? user?.credits ?? balance;

  const statusLabel = (status) => {
    const s = (status || "").toLowerCase();
    if (s === "waiting" || s === "confirming" || s === "sending") {
      return language === "tr" ? "bekliyor" : "waiting";
    }
    if (s === "paid" || s === "finished" || s === "confirmed") {
      return language === "tr" ? "ödendi" : "paid";
    }
    if (s === "failed" || s === "expired" || s === "cancelled" || s === "canceled") {
      return language === "tr" ? "başarısız" : "failed";
    }
    return status || "—";
  };

  const canDeleteTopup = (p) => {
    const s = (p.payment_status || "").toLowerCase();
    return !["paid", "finished", "confirmed"].includes(s);
  };

  const deleteTopup = async (id) => {
    if (!confirm(language === "tr" ? "Bu yükleme kaydı silinsin mi?" : "Delete this top-up record?")) return;
    try {
      await api.delete(`/me/api-balance/topups/${id}`);
      toast.success(language === "tr" ? "Silindi" : "Deleted");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || (language === "tr" ? "Silinemedi" : "Delete failed"));
    }
  };

  return (
    <CustomerLayout activeId="api-balance">
      <div className="w-full max-w-3xl mx-auto px-4 sm:px-6 md:px-8 py-6 md:py-10">
        <div className="mb-8 text-center">
          <h1 className="text-2xl md:text-3xl font-mono font-bold mb-2 flex items-center justify-center gap-3">
            <Wallet className="w-7 h-7 text-primary flex-shrink-0" />
            {language === "tr" ? "API BAKİYE" : "API BALANCE"}
          </h1>
          <p className="text-muted-foreground font-mono text-sm max-w-xl mx-auto">
            {language === "tr"
              ? `API ile link basımı için bakiye yükleyin. 1 basım = $${pushPrice} (max 3 keyword).`
              : `Top up balance for API link pushes. 1 push = $${pushPrice} (max 3 keywords).`}
          </p>
        </div>

        <div className="glass p-6 sm:p-8 mb-6 text-center">
          <div className="text-xs font-mono uppercase text-muted-foreground mb-2">
            {language === "tr" ? "Mevcut Bakiye" : "Current Balance"}
          </div>
          <div className="text-4xl sm:text-5xl font-bold text-primary font-mono break-all">
            {loading ? "…" : `$${Number(displayBal).toFixed(2)}`}
          </div>
          <div className="text-xs text-muted-foreground mt-2 font-mono">
            ≈ {loading ? "…" : Math.floor(Number(displayBal) / pushPrice)}{" "}
            {language === "tr" ? "basım hakkı" : "pushes"}
          </div>
        </div>

        <div className="glass p-5 sm:p-6 mb-6">
          <h2 className="text-sm font-mono uppercase text-muted-foreground mb-4 text-center">
            {language === "tr" ? "Kripto ile Yükle" : "Top up with Crypto"}
          </h2>
          <div className="flex flex-col sm:flex-row gap-2 max-w-md mx-auto">
            <Input
              type="number"
              min={minTopup}
              max={maxTopup}
              step="1"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="bg-black/50 border-white/10 font-mono text-center sm:text-left"
              placeholder="100"
            />
            <Button
              onClick={topup}
              disabled={paying}
              className="font-mono bg-primary text-black whitespace-nowrap rounded-none"
            >
              {paying
                ? (language === "tr" ? "YÖNLENDİRİLİYOR…" : "REDIRECTING…")
                : (language === "tr" ? "ÖDEMEYE GİT" : "PAY NOW")}
            </Button>
          </div>
          <div className="flex flex-wrap justify-center gap-2 mt-4">
            {[50, 100, 250, 500, 1000].map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => setAmount(String(n))}
                className="text-xs font-mono px-3 py-1.5 border border-white/10 hover:border-primary/50 text-muted-foreground hover:text-primary"
              >
                ${n}
              </button>
            ))}
          </div>
        </div>

        <div className="glass overflow-hidden">
          <div className="px-5 py-3 border-b border-white/10 text-xs font-mono uppercase text-muted-foreground text-center">
            {language === "tr" ? "Son Yüklemeler" : "Recent Top-ups"}
          </div>
          {recent.length === 0 ? (
            <div className="p-5 text-sm text-muted-foreground font-mono text-center">
              {language === "tr" ? "Henüz yükleme yok" : "No top-ups yet"}
            </div>
          ) : (
            <div className="divide-y divide-white/5">
              {recent.map((p) => (
                <div key={p.id} className="px-4 sm:px-5 py-3 flex flex-wrap items-center justify-between gap-2 text-sm font-mono">
                  <span>${Number(p.amount || 0).toFixed(2)}</span>
                  <span
                    className={
                      canDeleteTopup(p) && (p.payment_status || "").toLowerCase() === "failed"
                        ? "text-red-400"
                        : canDeleteTopup(p)
                          ? "text-amber-400"
                          : "text-emerald-400"
                    }
                  >
                    {statusLabel(p.payment_status)}
                  </span>
                  <span className="text-muted-foreground text-xs">
                    {(p.paid_at || p.created_at || "").slice(0, 19)}
                  </span>
                  {canDeleteTopup(p) ? (
                    <Button
                      size="icon"
                      variant="ghost"
                      className="text-red-400 h-8 w-8"
                      onClick={() => deleteTopup(p.id)}
                      title={language === "tr" ? "Sil" : "Delete"}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  ) : (
                    <span className="w-8" />
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </CustomerLayout>
  );
};

export default ApiBalancePage;
